import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Facet, FacetValue, RoutingService, TranslationService } from '@spartacus/core';
import { FacetCollapseState, FacetComponent, FacetService } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { LocalStorageService } from '../../../../../../core/services/localStorage.service';
import { FacetFilter } from './facetsFilter.model';

@Component({
  selector: 'cx-facet',
  templateUrl: './facet.component.html',
  styleUrl: './facet.component.scss',
  standalone: false,
})
export class CustomFacetComponent extends FacetComponent implements OnInit, OnDestroy {
  @Input() override set facet(value: Facet) {
    this._facet = value;
    this.originalValues = this._facet.values;
    this.currentFacetValues$.next(this.originalValues);
    this.isMultiSelect = !!value.multiSelect;
    this.state$ = this.facetService.getState(value);
  }

  subscription = new Subscription();
  facetState$ = new BehaviorSubject<FacetCollapseState>({});
  originalValues: FacetValue[] | undefined = [];
  currentFacetValues$ = new BehaviorSubject<FacetValue[] | undefined>([]);
  localFilters: FacetFilter[] = JSON.parse(this.localStorageService.getItem('facetsFilter') ?? '[]');
  localFilter?: FacetFilter;

  displayFacetFormError$ = new BehaviorSubject<string[]>([]);
  customFacets_filter_errors_fromBiggerThanTo: string = '';

  override get facet(): Facet {
    return {
      ...this._facet,
      // enableFacetFilter: this._facet?.code === 'outputHP' || this._facet?.code === 'ipCode',
      enableFacetFilter: this._facet?.enableFacetFilter,
    };
  }

  form: FormGroup = this.fb.group({
    from: ['', []],
    to: ['', []],
  });

  constructor(
    private fb: FormBuilder,
    protected _facetService: FacetService,
    protected _elementRef: ElementRef<HTMLElement>,
    protected _cd: ChangeDetectorRef,
    protected routingService: RoutingService,
    private localStorageService: LocalStorageService,
    private translationService: TranslationService,
  ) {
    super(_facetService, _elementRef, _cd);
  }

  ngOnInit(): void {
    this.populateComponent();
    this.getTranslation();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getTranslation() {
    this.subscription.add(
      combineLatest([this.translationService.translate('customFacets.filter.errors.fromBiggerThanTo')]).subscribe(
        ([customFacets_filter_errors_fromBiggerThanTo]) => {
          this.customFacets_filter_errors_fromBiggerThanTo = customFacets_filter_errors_fromBiggerThanTo;
        },
      ),
    );
  }

  populateComponent() {
    this.subscription.add(
      this.state$.subscribe(state => {
        this.facetState$.next(state);
      }),
    );

    if (this.facet?.enableFacetFilter) {
      this.localFilter = this.localFilters.find(filter => filter.code === this.facet?.code);

      const from = this.localFilter?.from || '';
      const to = this.localFilter?.to || '';

      this.form.get('from')?.setValue(from);
      this.form.get('to')?.setValue(to);
      this.submit();
    }
  }

  reset() {
    this.localFilters = this.localFilters.filter(filter => filter.code !== this.facet.code);
    this.localStorageService.setItem('facetsFilter', JSON.stringify(this.localFilters));
    this.form.get('from')?.setValue('');
    this.form.get('to')?.setValue('');
    this.currentFacetValues$.next(this.originalValues);
    this.form.reset();
  }

  getValue(type: 'minor' | 'major'): number {
    const preventNumber = type === 'major' ? 0 : 1000000000;

    return (
      this.originalValues?.reduce((acc, value) => {
        const number = parseFloat(value.name ?? '0');

        if (type === 'minor') {
          if (number < acc) {
            return number;
          }
        }

        if (type === 'major') {
          if (number > acc) {
            return number;
          }
        }

        return acc;
      }, preventNumber) ?? 0
    );
  }

  submit() {
    this.form.markAllAsTouched();
    this.displayFacetFormError$.next([]);

    const values = this.form.value;
    const from = parseFloat(values.from) || this.getValue('minor');
    const to = parseFloat(values.to) || this.getValue('major');

    if (parseFloat(values.from) > parseFloat(values.to)) {
      this.displayFacetFormError$.next([this.customFacets_filter_errors_fromBiggerThanTo]);
      return;
    }

    this.setFacetToStore({
      code: this.facet.code!,
      from: values.from || '',
      to: values.to || '',
    });

    if (from && to) {
      const newFacets = this.originalValues?.filter(
        value => value.name && parseFloat(value.name) >= from && parseFloat(value.name) <= to,
      );

      this.currentFacetValues$.next(newFacets);
    } else {
      this.currentFacetValues$.next(this.originalValues);
    }
  }

  setFacetToStore(facetFilter: FacetFilter) {
    this.localFilter = this.localFilters.find(filter => filter.code === facetFilter.code);

    if (this.localFilter) {
      this.localFilter.from = facetFilter.from;
      this.localFilter.to = facetFilter.to;
    } else {
      this.localFilters.push(facetFilter);
    }

    this.localStorageService.setItem('facetsFilter', JSON.stringify(this.localFilters));
  }

  getMaxItens(): number {
    if (this.facet.enableFacetFilter) {
      return 10000;
    }

    return this.facetState$.getValue()!.topVisible ?? 6;
  }
}
